import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Button, Card, DataTable, EmptyState, FilterBar, FilterChips, Pagination, Pill, SelectField, Tabs,
  type Column, fmtBRL,
} from "@atlas/ui/web";
import type {
  AdminEstadoDaFolha,
  ArquivoTrocado,
  PrefeituraFolha,
  PrefeituraUnidade,
  ResumoValidacao,
  SituacaoArquivo,
  ValidacaoRetorno,
} from "@atlas/sdk";
import { lerReconhecimentoRecusado } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { useUnidadeAtiva } from "../../lib/unidade-ativa";
import { Corta, Field, PageHeader, downloadAuthed, inp } from "./_ui";
import { motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { compParaInput, filtrarFolhas } from "../../lib/filtro-folhas";
import { useFiltros } from "../../lib/use-filtros";
import { ModalRelatorio, RelatorioConversaoPainel, type IdentidadeLayout } from "./troca-arquivos-relatorio";
import { LayoutChip, ehCodigoPadrao } from "../../components/LayoutChip";
import { AndamentoEnvio } from "../../components/AndamentoEnvio";

/**
 * TROCA DE ARQUIVOS — envio, validação e acompanhamento dos arquivos da folha.
 *
 * A direção nunca inverte:
 *
 *   Atlas gera o MOVIMENTO → a prefeitura baixa → processa a folha
 *   → a prefeitura envia o RETORNO → a Atlas valida e processa
 *
 * A prefeitura **não envia movimento**. Por isso a aba de envio mostra o tipo
 * fixo em vez de um select: um campo escolhível sugeriria que existe a outra
 * opção.
 *
 * A aba fica na URL (`?aba=`) para o link ser compartilhável e o botão voltar do
 * navegador funcionar. `?competencia=` chega da tela de Folhas.
 */

const ABAS = [
  // COMPETENCIAS vem primeiro porque e' a pergunta que a prefeitura faz ao abrir a
  // tela: "em que pe' esta o mes?". As outras abas sao o que ela FAZ depois de
  // saber isso.
  //
  // Esta aba absorveu a rota /prefeitura/folhas, que virou uma tela sem funcao
  // depois que Troca de arquivos assumiu o envio e o historico: o que sobrava nela
  // era um CRUD de folha mais tres botoes que levavam para ca'. Manter as duas
  // telas obrigava a prefeitura a saber em qual delas cada metade do ciclo mora.
  { key: "competencias", label: "Competências" },
  { key: "recentes", label: "Recentes" },
  { key: "enviar", label: "Enviar retorno" },
  { key: "historico", label: "Histórico" },
  { key: "validar", label: "Validar arquivo" },
];

const MESES = [
  "",
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];
const fmtCompetencia = (c: string) => `${MESES[Number(c.slice(4, 6))] ?? c} de ${c.slice(0, 4)}`;

const fmtQuando = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR")} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * Rótulo e cor de cada situação. Fonte única — tabela e filtro leem daqui.
 *
 * `label` é curto porque cabe na coluna; `completo` é o que o filtro mostra e o
 * que vai no `title` da célula. "Processado com ressalvas" numa Pill sozinho
 * esticava a tabela além da tela.
 */
const SITUACAO: Record<
  SituacaoArquivo,
  { label: string; completo: string; variant: "averbado" | "aceita" | "pendente" | "rejeitada" | "emdia" }
> = {
  gerado: { label: "Gerado", completo: "Gerado", variant: "averbado" },
  recebido: { label: "Recebido", completo: "Recebido", variant: "pendente" },
  validando: { label: "Validando", completo: "Validando", variant: "pendente" },
  valido: { label: "Válido", completo: "Válido (não importado)", variant: "emdia" },
  processado: { label: "Processado", completo: "Processado", variant: "aceita" },
  processado_com_ressalvas: {
    label: "Com ressalvas",
    completo: "Processado com ressalvas",
    variant: "pendente",
  },
  rejeitado: { label: "Rejeitado", completo: "Rejeitado", variant: "rejeitada" },
};

const situacaoDe = (s: string) =>
  SITUACAO[s as SituacaoArquivo] ?? { label: s, completo: s, variant: "pendente" as const };

/**
 * Grade dos filtros dentro do `FilterBar`.
 *
 * O FilterBar renderiza `children` como bloco solto: sem isto, cada select
 * ocupa a largura inteira e os quatro empilham numa coluna gigante. O
 * `auto-fit` com mínimo de 180px põe quatro lado a lado no desktop e quebra
 * sozinho no celular.
 */
const GRADE_FILTROS: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
};

export function PrefeituraTrocaArquivos() {
  const [params, setParams] = useSearchParams();
  const aba = params.get("aba") ?? "recentes";
  const competenciaDaUrl = params.get("competencia") ?? "";

  const trocarAba = (k: string) => {
    const p = new URLSearchParams(params);
    p.set("aba", k);
    setParams(p, { replace: true });
  };

  return (
    // `minmax(0, 1fr)` (T081): a coluna implícita de um grid é `auto` e cresce
    // até o conteúdo mais largo — as tabelas desta tela empurrariam a PÁGINA
    // para a direita a 1024 px. Com o mínimo em zero, quem rola é o quadro da
    // tabela e a página fica quieta (FR-038).
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <PageHeader
        title="Troca de arquivos"
        subtitle="Envio, validação e acompanhamento dos arquivos da folha de pagamento."
      />
      <Tabs tabs={ABAS} activeKey={aba} onChange={trocarAba} />

      {aba === "competencias" ? <Competencias /> : null}
      {aba === "recentes" ? <Recentes competenciaInicial={competenciaDaUrl} /> : null}
      {aba === "enviar" ? <EnviarRetorno competenciaInicial={competenciaDaUrl} modo="enviar" /> : null}
      {aba === "validar" ? <EnviarRetorno competenciaInicial={competenciaDaUrl} modo="validar" /> : null}
      {aba === "historico" ? <Historico competenciaInicial={competenciaDaUrl} /> : null}
    </div>
  );
}

// ============================================================ comum

/**
 * Colunas densas de propósito.
 *
 * A primeira versão tinha oito colunas — tipo, folha, competência, arquivo,
 * inclusão, situação, observação, ações — e todas `nowrap` (o default da
 * `DataTable`). Um nome como `movimento_I.M.A.M.N. - Instituto de Meio
 * Ambiente_202608.csv` sozinho esticava a tabela para além da tela, e só dava
 * para ler arrastando de lado.
 *
 * A correção junta o que é do mesmo assunto numa célula empilhada e corta o que
 * é longo. Nada some: o nome inteiro está no `title`, e a situação abreviada
 * também.
 */
function colunasBase(): Column<ArquivoTrocado>[] {
  return [
    {
      key: "arquivo",
      header: "Arquivo",
      width: 200,
      render: (a) => (
        <div style={{ display: "grid", gap: 3, justifyItems: "start" }}>
          {a.tipo === "movimento" ? (
            <Pill variant="averbado">Movimento</Pill>
          ) : (
            <Pill variant="aceita">Retorno</Pill>
          )}
          <Corta
            largura={188}
            titulo={a.arquivoNome}
            estilo={{ fontSize: 11, opacity: 0.7, fontFamily: "var(--font-mono)" }}
          >
            {a.arquivoNome || "—"}
          </Corta>
        </div>
      ),
    },
    {
      key: "folha",
      header: "Folha / competência",
      width: 150,
      render: (a) => (
        <div style={{ display: "grid", gap: 2 }}>
          <Corta largura={138} titulo={fmtCompetencia(a.competencia)} estilo={{ fontSize: 13 }}>
            {fmtCompetencia(a.competencia)}
          </Corta>
          <Corta largura={138} titulo={a.parte ?? ""} estilo={{ fontSize: 11, opacity: 0.7 }}>
            {a.parte || "arquivo único"}
          </Corta>
        </div>
      ),
    },
    {
      key: "inclusao",
      header: "Inclusão",
      width: 150,
      render: (a) => (
        <div style={{ display: "grid", gap: 2 }}>
          <Corta largura={138} titulo={a.atorNome} estilo={{ fontSize: 12 }}>
            {a.atorNome}
          </Corta>
          <span style={{ fontSize: 11, opacity: 0.7, whiteSpace: "nowrap" }}>{fmtQuando(a.recebidoEm)}</span>
        </div>
      ),
    },
    {
      key: "layout",
      header: "Layout",
      width: 190,
      wrap: true,
      // A VERSAO E' A DA EPOCA, gravada no registro — nao a atual do catalogo.
      // O arquivo foi lido sob aquela regra, e e' por isso que a atribuicao tem
      // vigencia por competencia em vez de sobrescrever.
      //
      // O CHIP E' O MESMO DO PAINEL DE RELATORIO E DO CARTAO DE VIGENTES
      // (FR-036). Sem `comLink`: a ficha do layout vive sob /averbadora.
      render: (a) =>
        a.layout ? (
          <LayoutChip
            tamanho="sm"
            nome={a.layout.nome}
            codigo={a.layout.codigo}
            versao={a.layout.versao}
            padrao={ehCodigoPadrao(a.layout.codigo)}
          />
        ) : (
          <span style={{ opacity: 0.4 }} title="Registro anterior ao catálogo de layouts.">
            —
          </span>
        ),
    },
    {
      key: "situacao",
      header: "Situação",
      width: 120,
      render: (a) => {
        const s = situacaoDe(a.status);
        // O rótulo curto cabe na coluna; o completo fica no `title`, para
        // "Com ressalvas" não virar um estado que ninguém sabe o que é.
        return (
          <span title={s.completo}>
            <Pill variant={s.variant}>{s.label}</Pill>
          </span>
        );
      },
    },
  ];
}

/** Erros e avisos guardados no resumo, em frase curta. */
function ressalvas(a: ArquivoTrocado): string[] {
  const r = a.resumo ?? {};
  const n = (k: string) => Number(r[k] ?? 0);
  const fora: string[] = [];
  if (n("conflitosEntrePartes")) fora.push(`${n("conflitosEntrePartes")} conflito(s) entre folhas`);
  if (n("instruidosSemRetorno")) fora.push(`${n("instruidosSemRetorno")} instruído(s) sem retorno`);
  if (n("linhasRejeitadas")) fora.push(`${n("linhasRejeitadas")} linha(s) rejeitada(s)`);
  if (n("naoSolicitados")) fora.push(`${n("naoSolicitados")} em conciliação`);
  // O QUE A TRADUCAO CUSTOU, na mesma linha do resto. `dividas_somadas` no
  // movimento e `descartadas`/`desligamentos_derivados` no retorno sao ressalva
  // pelo mesmo motivo dos outros itens: nao impedem o ciclo, e alguem precisa
  // olhar. O relatorio completo abre pelo botao ao lado.
  if (n("dividas_somadas")) fora.push(`${n("dividas_somadas")} dívida(s) somada(s) no layout do órgão`);
  if (n("descartadas")) fora.push(`${n("descartadas")} linha(s) descartada(s) na tradução`);
  if (n("desligamentos_derivados")) fora.push(`${n("desligamentos_derivados")} desligamento(s) derivado(s)`);
  if (r.motivo) fora.push(String(r.motivo));
  return fora;
}

/**
 * O registro cujo relatório está aberto, e o modal dele.
 *
 * Vive num hook porque as duas tabelas da tela (Recentes e Histórico) precisam
 * do mesmo comportamento, e duplicar estado entre elas daria dois modais
 * possíveis ao mesmo tempo.
 */
function useRelatorioAberto() {
  const [aberto, setAberto] = useState<ArquivoTrocado | null>(null);
  const modal = aberto ? (
    <ModalRelatorio
      arquivo={aberto}
      buscar={(id) => atlas.prefeitura.trocaArquivoRelatorio(id)}
      onClose={() => setAberto(null)}
    />
  ) : null;
  return { abrir: setAberto, modal };
}

/** O aviso vem ANTES do clique, enquanto ainda dá para desistir. */
async function pedirMotivoEExcluir(a: ArquivoTrocado) {
  const grave = a.tipo === "retorno" && a.status.startsWith("processado");
  const motivo = window.prompt(
    grave
      ? "Este retorno JÁ FOI PROCESSADO. Excluí-lo tira o arquivo do histórico, mas NÃO desfaz os descontos registrados nem um desligamento declarado. Para corrigir, reenvie a mesma folha com o arquivo certo.\n\nPor que está excluindo?"
      : "Por que está excluindo este arquivo?",
    "",
  );
  if (motivo === null) return null;
  if (motivo.trim().length < 3) {
    window.alert("Escreva o motivo — ele fica no histórico.");
    return null;
  }
  return atlas.prefeitura.excluirArquivoTrocado(a.id, motivo.trim());
}

function Acoes({
  arquivo,
  onExcluir,
  onRelatorio,
}: {
  arquivo: ArquivoTrocado;
  onExcluir: () => void;
  onRelatorio: () => void;
}) {
  // O movimento e' gerado POR UNIDADE: sem a organizacao ativa, este botao
  // baixaria o arquivo da organizacao do login mesmo quando a tela esta
  // mostrando o historico de uma atendida.
  const { opcoesDoSdk } = useUnidadeAtiva();
  // O PADRAO SO' APARECE QUANDO EXISTE UM SEGUNDO ARQUIVO.
  //
  // A condicao e' `temCanonico`, e nao "o layout nao e' o padrao": os dois quase
  // sempre coincidem, mas divergem no registro emitido ANTES de a unidade
  // ganhar layout adaptado — ali o canonico nunca foi guardado. Oferecer o
  // botao nesse caso entregaria uma geracao AO VIVO com cara de arquivo
  // emitido, que e' exatamente o que a Troca de Arquivos existe para nao fazer.
  const temSegundoArquivo = arquivo.tipo === "movimento" && !!arquivo.temCanonico;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {arquivo.arquivoKey ? (
        <Button
          size="sm"
          variant="ghost"
          title="Baixa o arquivo que foi trocado naquele dia."
          onClick={() => downloadAuthed(atlas.prefeitura.trocaArquivoUrl(arquivo.id), arquivo.arquivoNome)}
        >
          ↓ Baixar
        </Button>
      ) : arquivo.tipo === "movimento" ? (
        <Button
          size="sm"
          variant="ghost"
          title="Este registro não tem arquivo guardado. Gera o movimento AGORA — pode diferir do que foi entregue naquele dia, porque ele é sempre corrente."
          onClick={() =>
            downloadAuthed(
              atlas.prefeitura.movimentoUrl(arquivo.competencia, opcoesDoSdk()),
              `movimento_${arquivo.competencia}.csv`,
            )
          }
        >
          ↻ Gerar agora
        </Button>
      ) : (
        <span style={{ opacity: 0.4 }}>—</span>
      )}

      {temSegundoArquivo ? (
        <Button
          size="sm"
          variant="ghost"
          title="O MESMO movimento no padrão Atlas. Ele continua disponível mesmo com um layout adaptado atribuído."
          onClick={() =>
            downloadAuthed(
              atlas.prefeitura.movimentoUrl(arquivo.competencia, { layout: "atlas", ...opcoesDoSdk() }),
              `movimento_${arquivo.competencia}.csv`,
            )
          }
        >
          padrão Atlas
        </Button>
      ) : null}

      {/* `temRelatorio` decide ANTES do clique: registro legado e recusa por
          reconhecimento nao tem relatorio, e um botao que abre para dar 404
          seria pior que botao nenhum. */}
      {arquivo.temRelatorio ? (
        <Button
          size="sm"
          variant="ghost"
          title={
            arquivo.tipo === "movimento"
              ? "O que a escrita no layout do órgão custou: dívidas somadas e campos sem lugar."
              : "O que a tradução leu, gerou, descartou e derivou neste arquivo."
          }
          onClick={onRelatorio}
        >
          Relatório
        </Button>
      ) : null}

      {arquivo.arquivoKey ? (
        <Button
          size="sm"
          variant="ghost"
          // O botão é só um "✕": sem `aria-label` o leitor de tela anuncia
          // "botão" e mais nada, e o `title` não é substituto — ele nem sempre
          // é lido, e nunca aparece para quem navega por teclado (T081, FR-039).
          aria-label={`Excluir do histórico o arquivo ${arquivo.arquivoNome}`}
          title="Tira o arquivo do histórico. NÃO desfaz o que ele aplicou."
          onClick={onExcluir}
        >
          ✕
        </Button>
      ) : null}
    </div>
  );
}

// ============================================================ Recentes

function Recentes({ competenciaInicial }: { competenciaInicial: string }) {
  const qc = useQueryClient();
  const [competencia, setCompetencia] = useState(competenciaInicial);
  const relatorio = useRelatorioAberto();

  const q = useQuery({
    queryKey: ["prefeitura", "troca-arquivos", "recentes", competencia],
    queryFn: () =>
      atlas.prefeitura.trocaArquivos({ competencia: competencia || undefined, offset: 0, limite: 6 }),
  });

  const excluir = useMutation({
    mutationFn: (a: ArquivoTrocado) => pedirMotivoEExcluir(a),
    onSuccess: (r) => {
      if (!r) return;
      if (r.aviso) window.alert(r.aviso);
      qc.invalidateQueries({ queryKey: ["prefeitura", "troca-arquivos"] });
    },
  });

  const columns: Column<ArquivoTrocado>[] = [
    ...colunasBase(),
    {
      key: "acoes",
      header: "Ações",
      width: 180,
      render: (a) => (
        <Acoes arquivo={a} onExcluir={() => excluir.mutate(a)} onRelatorio={() => relatorio.abrir(a)} />
      ),
    },
  ];

  return (
    // Sem `Card` em volta: o Card ja tem padding 24 e a DataTable tem o dela —
    // aninhar os dois afasta a tabela das bordas e desalinha com as outras
    // telas, que poem a DataTable solta.
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <strong style={{ fontSize: 14 }}>Últimos 6 arquivos</strong>
        {competencia ? (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Filtrado por {fmtCompetencia(competencia)}{" "}
            <Button size="sm" variant="ghost" onClick={() => setCompetencia("")}>
              limpar
            </Button>
          </span>
        ) : null}
      </div>
      <DataTable
        caption="Os seis arquivos trocados mais recentes, com competência, tipo, quem enviou e a situação"
        columns={columns}
        rows={q.data?.arquivos ?? []}
        rowKey={(a) => a.id}
        loading={q.isLoading}
        emptyState={q.isError ? "Não foi possível carregar." : "Nenhum arquivo trocado ainda."}
      />
      {relatorio.modal}
    </div>
  );
}

// ============================================================ Enviar / Validar

/**
 * Os dois avisos do portão de desligamento derivado, por id.
 *
 * Existem como constante porque o `aria-describedby` do botão os referencia: o
 * dia em que alguém renomear um id e esquecer o outro lado, o leitor de tela
 * simplesmente não anuncia nada — falha silenciosa, do tipo que só aparece com
 * quem depende dela.
 */
const ID_AVISO_DERIVADOS = "aviso-desligamentos-derivados";
const ID_PORTAO_DERIVADOS = "portao-desligamentos-derivados";

/**
 * O nome e o "é o padrão?" de um layout, tirados dos vigentes da unidade.
 *
 * O relatório de conversão grava só código e versão (é o que valia na hora), e
 * o chip do cabeçalho quer a identidade inteira. A unidade escolhida já tem os
 * dois sentidos carregados na tela, então dá para completar sem consulta nova —
 * e sem depender do catálogo, que a prefeitura não enxerga.
 */
function identidadeDoLayout(
  codigo: string,
  unidade: PrefeituraUnidade | undefined,
): IdentidadeLayout | undefined {
  const vigente = [unidade?.layouts?.entrada, unidade?.layouts?.saida].find((l) => l?.codigo === codigo);
  if (!vigente) return undefined;
  return { nome: vigente.nome, padrao: vigente.padrao };
}



function EnviarRetorno({
  competenciaInicial,
  modo,
}: {
  competenciaInicial: string;
  modo: "enviar" | "validar";
}) {
  const qc = useQueryClient();
  // A ORGANIZAÇÃO VEM DO SELETOR DO CABEÇALHO (feature 009), e não mais de um
  // seletor local desta tela.
  //
  // Havia dois lugares dizendo sobre qual folha o operador estava agindo: este
  // campo e o shell. Dois seletores para a mesma pergunta é como o arquivo da
  // autarquia sobe na prefeitura — o cabeçalho diz uma coisa e o formulário
  // faz outra, e nada na tela denuncia a divergência.
  //
  // O campo continua existindo para as PARTES, que são recorte DENTRO da
  // unidade (Morada Nova manda `PREFEITURA` e `PISO ENFERMAGEM` em arquivos
  // separados) — e essa escolha o shell não tem como fazer.
  const { unidade, opcoesDoSdk, chaveDaUnidade } = useUnidadeAtiva();
  const unidadeId = unidade?.id;
  const partes = unidade?.partesDoRetorno ?? [];

  /** Qual recorte do retorno está subindo. Vazio = a unidade manda arquivo único. */
  const [lote, setLote] = useState("");
  const [observacao, setObservacao] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [validacao, setValidacao] = useState<ValidacaoRetorno | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // A RECUSA POR RECONHECIMENTO tem os dois lados (o que o layout esperava, o
  // que o zip trouxe) e eles nao cabem numa frase — sem isso a tela dizia
  // "arquivo invalido" para quem subiu o pacote certo da unidade errada.
  const [recusa, setRecusa] = useState<ReturnType<typeof lerReconhecimentoRecusado>>(null);
  // O PORTAO DE FR-019. Desligamento derivado nao passa sem alguem ter ABERTO a
  // lista — o efeito e' irreversivel, e "estava na tela" nao e' o mesmo que
  // "foi visto".
  const [derivadosVistos, setDerivadosVistos] = useState(false);

  // Sem partes declaradas, não há o que escolher: a unidade manda o retorno num
  // arquivo só, e `parte` fica vazia. Com partes, escolher é obrigatório — sem
  // ela a segunda remessa do mês marca a primeira como falha em folha.
  const parte = partes.length > 0 ? lote : "";
  const loteAtual = unidadeId != null && (partes.length === 0 || lote !== "");

  // Trocar de organização no cabeçalho zera a parte: o recorte é DAQUELA folha,
  // e carregá-lo para a próxima subiria o arquivo com o rótulo de outra.
  useEffect(() => {
    setLote("");
  }, [unidadeId]);
  // Uma parte só = nada a escolher.
  useEffect(() => {
    if (!lote && partes.length === 1) setLote(partes[0]!);
  }, [lote, partes]);

  const [folhaId, setFolhaId] = useState("");
  const folhas = useQuery({
    queryKey: ["prefeitura", "folhas", ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.folhas(opcoesDoSdk()),
    enabled: unidadeId != null,
  });

  const lista = folhas.data?.folhas ?? [];

  // A competência vem da tela de Folhas pela URL. Pré-seleciona a folha
  // correspondente — o operador clicou numa linha, não deve procurá-la de novo.
  useEffect(() => {
    if (folhaId || !competenciaInicial || lista.length === 0) return;
    const achou = lista.find((f) => f.competencia === competenciaInicial);
    if (achou) setFolhaId(achou.id);
  }, [competenciaInicial, lista, folhaId]);

  const validar = useMutation({
    mutationFn: async () => {
      setErro(null);
      setRecusa(null);
      return atlas.prefeitura.validarRetornoZip(folhaId, await arquivo!.arrayBuffer());
    },
    onSuccess: (r) => {
      setValidacao(r);
      // Cada validacao recomeça o portão: trocar de arquivo e reaproveitar um
      // "já vi" anterior seria confirmar desligamento de outro pacote.
      setDerivadosVistos(false);
    },
    onError: (e) => {
      setValidacao(null);
      setRecusa(lerReconhecimentoRecusado(e));
      setErro((e as Error).message);
    },
  });

  const enviar = useMutation({
    mutationFn: async () => {
      setErro(null);
      setRecusa(null);
      return atlas.prefeitura.enviarRetornoZip(folhaId, await arquivo!.arrayBuffer(), {
        parte: parte || undefined,
        observacao: observacao.trim() || undefined,
        // So' vai quando o operador abriu a lista. O `422` do servidor e' a
        // rede de seguranca; este parametro e' a confirmacao de que houve
        // alguem olhando.
        confirmarDerivados: exigeDerivados ? true : undefined,
      });
    },
    onSuccess: (r) => {
      // Modo manual (o default): o arquivo foi RECEBIDO e guardado, mas os
      // descontos ainda NAO foram aplicados. E' sucesso — nao "processado".
      if ("recebido" in r) {
        setArquivo(null);
        setValidacao(null);
        setDerivadosVistos(false);
        qc.invalidateQueries({ queryKey: ["prefeitura"] });
        window.alert(
          r.mensagem ??
            "Arquivo recebido e guardado. Os descontos ainda NÃO foram aplicados — a Atlas processa e confirma.",
        );
        return;
      }
      // Modo automatico: o pacote pode ter sido recusado por inteiro.
      if (r.rejeitado) {
        setErro(r.motivo_rejeicao ?? "O retorno foi recusado — nada foi aplicado.");
        return;
      }
      setArquivo(null);
      setValidacao(null);
      setDerivadosVistos(false);
      qc.invalidateQueries({ queryKey: ["prefeitura"] });
      window.alert("Retorno processado. Veja o resultado na aba Recentes.");
    },
    onError: (e) => {
      setRecusa(lerReconhecimentoRecusado(e));
      setErro((e as Error).message);
    },
  });

  const pronto = !!folhaId && !!arquivo;
  const exigeDerivados = (validacao?.exigeConfirmacao.desligamentosDerivados ?? 0) > 0;
  const podeImportar =
    !!validacao?.resumo.valido && !validacao.jaProcessado && (!exigeDerivados || derivadosVistos);
  // Enquanto o portão está fechado, o leitor de tela ouve a consequência E o
  // que destrava; depois de aberto, só a consequência continua valendo.
  const idsDoAviso = derivadosVistos
    ? ID_AVISO_DERIVADOS
    : `${ID_AVISO_DERIVADOS} ${ID_PORTAO_DERIVADOS}`;

  return (
    <div style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {/* O CARTAO DE LAYOUT SAIU DAQUI em 08/09/2026 — item 14 do PENDENCIAS.
          Ele mostrava codigo de catalogo, versao e a marca "padrao" dos dois
          sentidos: vocabulario de quem ADMINISTRA o catalogo, que e' a
          averbadora. Quem opera o orgao quer enviar o arquivo do mes.

          Sairam junto o "documento" e o "Ensaiar um arquivo". Os dois serviam
          a quem envia, e a decisao foi consciente: essa conversa e' da
          averbadora com o orgao, fora do produto. O hub de layouts
          (/averbadora/layouts) continua com tudo, para quem responde por isso. */}
      <Card>
        {/* Campos em GRADE, não numa coluna só: numa coluna de 620px todo input
            fica com 620px, inclusive os curtos, e a tela parece um formulário
            de cadastro em vez de um envio de arquivo. */}
        <div
          style={{
            display: "grid",
            gap: 14,
            gridTemplateColumns: "minmax(170px, 230px) minmax(260px, 1fr)",
            maxWidth: 720,
            alignItems: "start",
          }}
        >
          <Field lbl="Tipo">
            {/* Fixo, não select: a prefeitura não envia movimento. Um campo
                escolhível sugeriria que existe a outra opção. */}
            <div style={{ ...inp, display: "flex", alignItems: "center", opacity: 0.7 }}>Retorno</div>
          </Field>

          {/* LOTE DA FOLHA — vem ANTES da competência porque é ele que decide
              QUAIS competências existem: cada unidade pagadora (a prefeitura, e
              cada autarquia vinculada a ela) tem folha própria.
              Uma unidade que manda o retorno repartido aparece uma vez por
              recorte — os dois na mesma lista porque escolher só a unidade, ou
              só o recorte, não diz onde o arquivo entra. */}
          {/* `para`/`id` em todo campo do envio (T081, FR-039): o `Field`
              envolve o campo, mas a ligação explícita é a que o teste de
              navegador confere e a que sobrevive a qualquer remontagem da
              grade. */}
          {/* A ORGANIZAÇÃO não é campo desta tela: quem a escolhe é o seletor do
              cabeçalho. Ela aparece aqui como TEXTO para o operador conferir
              antes de subir o arquivo — ver, e não escolher de novo. */}
          <Field lbl="Enviando para" para="envio-retorno-unidade">
            <div id="envio-retorno-unidade" style={{ ...inp, display: "flex", alignItems: "center" }}>
              {unidade ? unidade.nome : "Carregando…"}
            </div>
          </Field>

          {/* A PARTE é recorte DENTRO da unidade, e por isso continua sendo campo:
              o que o arquivo não menciona ele não afirma, e sem a parte a segunda
              remessa do mês marca a primeira como falha em folha. */}
          {partes.length > 0 ? (
            <Field
              lbl="Parte do retorno"
              required
              para="envio-retorno-lote"
              hint="Esta folha manda o retorno repartido. Diga qual recorte está subindo."
            >
              <select
                id="envio-retorno-lote"
                style={inp}
                value={lote}
                onChange={(e) => {
                  setLote(e.target.value);
                  setFolhaId("");
                  setValidacao(null);
                }}
              >
                <option value="">Selecione…</option>
                {partes.map((pp) => (
                  <option key={pp} value={pp}>
                    {pp}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          <Field lbl="Folha / competência" required para="envio-retorno-folha">
            <select
              id="envio-retorno-folha"
              style={inp}
              value={folhaId}
              disabled={!loteAtual}
              onChange={(e) => {
                setFolhaId(e.target.value);
                setValidacao(null);
              }}
            >
              <option value="">
                {!loteAtual ? "Escolha a parte primeiro" : folhas.isLoading ? "Carregando…" : "Selecione…"}
              </option>
              {lista.map((f) => (
                <option key={f.id} value={f.id}>
                  {fmtCompetencia(f.competencia)} — {f.status}
                </option>
              ))}
            </select>
          </Field>

          <Field
            lbl="Observação"
            para="envio-retorno-observacao"
            hint={`${observacao.length}/100 — aparece no histórico e distingue um envio do outro.`}
          >
            <input
              id="envio-retorno-observacao"
              style={inp}
              value={observacao}
              maxLength={100}
              onChange={(e) => setObservacao(e.target.value)}
            />
          </Field>

          <div style={{ gridColumn: "1 / -1" }}>
            <Field lbl="Arquivo (ZIP com carga, consignados e rubrica)" required para="envio-retorno-arquivo">
              <input
                id="envio-retorno-arquivo"
                type="file"
                accept=".zip"
                style={inp}
                onChange={(e) => {
                  setArquivo(e.target.files?.[0] ?? null);
                  setValidacao(null);
                }}
              />
            </Field>
          </div>

          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button disabled={!pronto || validar.isPending} onClick={() => validar.mutate()}>
              {validar.isPending ? "Validando…" : modo === "validar" ? "Validar arquivo" : "Validar e enviar"}
            </Button>
            {modo === "enviar" && validacao ? (
              <Button
                variant="success"
                disabled={!podeImportar || enviar.isPending}
                // O AVISO NÃO É DECORAÇÃO DO LADO DO BOTÃO: `aria-describedby`
                // faz o leitor de tela anunciar a consequência junto do rótulo,
                // que é o momento em que ela ainda pode mudar a decisão. Quem
                // navega por teclado chega ao botão sem passar pelo parágrafo.
                aria-describedby={exigeDerivados ? idsDoAviso : undefined}
                title={
                  podeImportar
                    ? undefined
                    : exigeDerivados && !derivadosVistos
                      ? "Abra a lista de desligamentos derivados no relatório de conversão antes de importar."
                      : "Corrija os erros antes de importar."
                }
                onClick={() => enviar.mutate()}
              >
                {enviar.isPending ? "Importando…" : "Confirmar importação"}
              </Button>
            ) : null}
          </div>

          {/* Enquanto a API não responde, o que a tela sabe é que continua
              esperando e há quanto tempo. A validação lê o zip inteiro e
              confere cada linha; a importação grava a competência inteira ou
              nada — nenhuma das duas tem resposta parcial que virasse barra. */}
          <div style={{ gridColumn: "1 / -1" }}>
            <AndamentoEnvio
              ativo={validar.isPending}
              verbo="Validando"
              expectativa="Um arquivo com alguns milhares de linhas leva alguns segundos."
              consequencia="Não feche esta página: o resultado da validação aparece aqui no fim."
            />
            <AndamentoEnvio
              ativo={enviar.isPending}
              verbo="Importando"
              expectativa="Uma folha com alguns milhares de servidores leva em torno de um minuto."
              consequencia="Não feche esta página: o retorno entra inteiro ou não entra, e a confirmação aparece no fim."
            />
          </div>

          {/* A FRASE ÚNICA DA CONSEQUÊNCIA (FR-019), sempre a mesma, com o
              número real. Vale antes e depois de a lista ser aberta: abrir a
              lista libera o botão, não desfaz o efeito. O número vem do
              servidor — é quantos desligamentos a tradução CONCLUIU, um por
              matrícula, e nenhum deles veio declarado no arquivo. */}
          {modo === "enviar" && exigeDerivados ? (
            <div style={{ gridColumn: "1 / -1", display: "grid", gap: 4 }}>
              <p id={ID_AVISO_DERIVADOS} style={{ margin: 0, fontSize: 13, color: "var(--warn-500)" }}>
                Ao confirmar, os {validacao!.exigeConfirmacao.desligamentosDerivados} contratos destas
                matrículas saem da folha e as consignatárias são avisadas. Isso não é reversível.
              </p>
              {!derivadosVistos ? (
                <p id={ID_PORTAO_DERIVADOS} style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
                  Abra a lista no relatório abaixo para liberar o envio.
                </p>
              ) : null}
            </div>
          ) : null}

          {modo === "validar" ? (
            <p style={{ gridColumn: "1 / -1", fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
              Esta aba <b>não importa nada</b>: nenhum contrato, parcela ou resultado é alterado.
            </p>
          ) : null}

          {erro ? (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: 10,
                borderRadius: 8,
                fontSize: 13,
                background: "color-mix(in srgb, var(--danger, #d33) 12%, transparent)",
              }}
            >
              {erro}
              {/* RECUSA POR RECONHECIMENTO: os dois lados, lado a lado.
                  "Arquivo inválido" nao ajuda quem subiu o pacote certo da
                  unidade errada — o que resolve e' ver o que era esperado e o
                  que veio. */}
              {recusa ? (
                <div
                  style={{
                    marginTop: 8,
                    display: "grid",
                    gap: 8,
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>
                      Esperado ({recusa.esperado.nome ?? recusa.esperado.layout})
                    </div>
                    <ul style={{ margin: "4px 0 0", paddingLeft: 16, fontSize: 12 }}>
                      {recusa.esperado.arquivos.map((a) => (
                        <li key={a} style={{ fontFamily: "var(--font-mono, monospace)" }}>
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>Encontrado no seu zip</div>
                    <ul style={{ margin: "4px 0 0", paddingLeft: 16, fontSize: 12 }}>
                      {recusa.encontrado.entradas.map((e, i) => (
                        <li key={e}>
                          <span style={{ fontFamily: "var(--font-mono, monospace)" }}>{e}</span>
                          {recusa.encontrado.cabecalhos[i] ? (
                            <div style={{ opacity: 0.75, fontSize: 11 }}>
                              1ª linha: {recusa.encontrado.cabecalhos[i]}
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      {validacao ? (
        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            alignItems: "start",
          }}
        >
          <ResultadoValidacao resumo={validacao.resumo} jaProcessado={validacao.jaProcessado} />
          {/* O nome e o "é o padrão?" vêm do layout EM VIGOR na unidade — o
              relatório guarda só código e versão. É o mesmo layout: a validação
              acabou de rodar com ele. */}
          <RelatorioConversaoPainel
            relatorio={validacao.relatorioConversao}
            identidade={identidadeDoLayout(
              validacao.relatorioConversao.layout.codigo,
              unidade ?? undefined,
            )}
            onDerivadosVistos={() => setDerivadosVistos(true)}
          />
        </div>
      ) : null}
    </div>
  );
}

function ResultadoValidacao({
  resumo,
  jaProcessado,
}: {
  resumo: ResumoValidacao;
  jaProcessado: { em: string; por: string; observacao: string } | null;
}) {
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <Pill variant={resumo.valido ? "aceita" : "rejeitada"}>{resumo.valido ? "Válido" : "Inválido"}</Pill>
        <strong style={{ fontSize: 14 }}>Resultado da validação</strong>
      </div>

      {jaProcessado ? (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            marginBottom: 10,
            fontSize: 13,
            background: "color-mix(in srgb, var(--gold-500) 14%, transparent)",
          }}
        >
          <b>Este arquivo já foi processado</b> em {fmtQuando(jaProcessado.em)} por {jaProcessado.por} (
          {jaProcessado.observacao}). Importar de novo daria baixa duas vezes na mesma parcela. Se quer
          reprocessar, exclua o envio anterior no histórico.
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 13, marginBottom: 12 }}>
        <span>
          Total de linhas: <b>{resumo.totalLinhas}</b>
        </span>
        <span>
          Válidos: <b>{resumo.registrosValidos}</b>
        </span>
        <span>
          Inválidos: <b>{resumo.registrosInvalidos}</b>
        </span>
        <span>
          Avisos: <b>{resumo.avisos.length}</b>
        </span>
        <span>
          Duplicidades: <b>{resumo.duplicidades.length}</b>
        </span>
        <span>
          Valor total: <b>{fmtBRL(resumo.valorTotal)}</b>
        </span>
      </div>

      {resumo.duplicidades.length > 0 ? (
        <Bloco titulo="Duplicidades — a mesma dívida em mais de uma linha" tom="erro">
          <p style={{ margin: "0 0 6px", fontSize: 12 }}>
            Importar daria baixa duas vezes na mesma parcela. Corrija o arquivo antes de enviar.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
            {resumo.duplicidades.map((d) => (
              <li key={d.adf}>
                Matrícula {d.matricula} · ADF {d.adf} nas linhas {d.linhas.join(", ")}
              </li>
            ))}
          </ul>
        </Bloco>
      ) : null}

      {resumo.erros.length > 0 ? (
        <Bloco titulo={`Erros (${resumo.erros.length})`} tom="erro">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
            {resumo.erros.slice(0, 50).map((e, i) => (
              <li key={i}>
                {e.linha > 0 ? <>Linha {e.linha} · </> : null}
                {e.arquivo} · <b>{e.campo}</b>: {e.mensagem}
              </li>
            ))}
          </ul>
          {resumo.erros.length > 50 ? (
            <p style={{ fontSize: 12, opacity: 0.7 }}>…e mais {resumo.erros.length - 50}.</p>
          ) : null}
        </Bloco>
      ) : null}

      {resumo.avisos.length > 0 ? (
        <Bloco titulo={`Avisos (${resumo.avisos.length})`} tom="aviso">
          <p style={{ margin: "0 0 6px", fontSize: 12 }}>
            Não impedem a importação — mas alguém precisa olhar depois.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
            {resumo.avisos.slice(0, 30).map((a, i) => (
              <li key={i}>
                Linha {a.linha} · matrícula {a.matricula}: {a.mensagem}
              </li>
            ))}
          </ul>
        </Bloco>
      ) : null}

      {resumo.matriculasDesconhecidas.length > 0 ? (
        <Bloco titulo="Matrículas que não existem nesta folha" tom="aviso">
          <p style={{ margin: "0 0 6px", fontSize: 12 }}>
            Matrícula é numerada por folha — estas não estão na base desta.
          </p>
          <span style={{ fontSize: 12, fontFamily: "monospace" }}>
            {resumo.matriculasDesconhecidas.slice(0, 30).join(", ")}
          </span>
        </Bloco>
      ) : null}
    </Card>
  );
}

function Bloco({ titulo, tom, children }: { titulo: string; tom: "erro" | "aviso"; children: ReactNode }) {
  const cor = tom === "erro" ? "var(--danger, #d33)" : "var(--gold-500)";
  return (
    <div
      style={{
        marginTop: 10,
        padding: 10,
        borderRadius: 8,
        border: `1px solid color-mix(in srgb, ${cor} 40%, transparent)`,
      }}
    >
      <strong style={{ fontSize: 13, color: cor }}>{titulo}</strong>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

// ============================================================ Histórico

function Historico({ competenciaInicial }: { competenciaInicial: string }) {
  const qc = useQueryClient();
  const relatorio = useRelatorioAberto();
  const [tipo, setTipo] = useState("");
  const [competencia, setCompetencia] = useState(competenciaInicial);
  const [ator, setAtor] = useState("");
  const [status, setStatus] = useState("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);

  const q = useQuery({
    queryKey: [
      "prefeitura",
      "troca-arquivos",
      "historico",
      tipo,
      competencia,
      ator,
      status,
      busca,
      pagina,
      porPagina,
    ],
    queryFn: () =>
      atlas.prefeitura.trocaArquivos({
        tipo: (tipo || undefined) as "movimento" | "retorno" | undefined,
        competencia: competencia || undefined,
        ator: ator || undefined,
        status: (status || undefined) as SituacaoArquivo | undefined,
        q: busca || undefined,
        offset: (pagina - 1) * porPagina,
        limite: porPagina,
      }),
  });

  const excluir = useMutation({
    mutationFn: (a: ArquivoTrocado) => pedirMotivoEExcluir(a),
    onSuccess: (r) => {
      if (!r) return;
      if (r.aviso) window.alert(r.aviso);
      qc.invalidateQueries({ queryKey: ["prefeitura", "troca-arquivos"] });
    },
  });


  const facetas = q.data?.facetas ?? { competencias: [], atores: [] };

  // Adotou `useFiltros` em 04/09/2026 por arrasto: a aba Competencias, no mesmo
  // arquivo, adotou, e a checagem de adocao do G10 e' POR ARQUIVO — entao o G9
  // passou a valer aqui tambem e cobrou as dimensoes que o inventario nao
  // declarava. Foram DUAS: `ator` e `status` filtram desde sempre e nao
  // estavam no inventario. E' o mesmo caso do `escopo` do Catalogo — filtro
  // real invisivel para o inventario, que so' o G9 enxerga.
  //
  // A reforma de CONTROLE desta aba (situacao em segmentado, usuario em pill,
  // intervalo de data/hora) e' o Grupo 1.6; aqui so' entrou a contabilidade.
  const filtros = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "tipo", ligado: tipo !== "", limpar: () => setTipo("") },
      { nome: "competencia", ligado: competencia !== "", limpar: () => setCompetencia("") },
      { nome: "ator", ligado: ator !== "", limpar: () => setAtor("") },
      { nome: "status", ligado: status !== "", limpar: () => setStatus("") },
    ],
    setPagina,
  );
  const { filtrar } = filtros;

  const columns: Column<ArquivoTrocado>[] = [
    ...colunasBase(),
    {
      key: "observacao",
      header: "Observação",
      wrap: true,
      // Única coluna que quebra linha, e com teto próprio: o default de `wrap`
      // na `DataTable` é 320px, largo demais quando já há quatro colunas antes.
      render: (a) => (
        <div style={{ display: "grid", gap: 2, maxWidth: 220 }}>
          <span>{a.observacao}</span>
          {ressalvas(a).map((r, i) => (
            <span key={i} style={{ fontSize: 11, opacity: 0.7 }}>
              {r}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: "acoes",
      header: "Ações",
      width: 180,
      render: (a) => (
        <Acoes arquivo={a} onExcluir={() => excluir.mutate(a)} onRelatorio={() => relatorio.abrir(a)} />
      ),
    },
  ];

  return (
    <div>
      <FilterBar
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        {...filtros.propsFilterBar}
      >
        <div style={GRADE_FILTROS}>
          <SelectField
            label="Tipo"
            value={tipo}
            onChange={(e) => filtrar(setTipo)(e.target.value)}
            options={[
              { value: "", label: "— TODOS —" },
              { value: "movimento", label: "Movimento" },
              { value: "retorno", label: "Retorno" },
            ]}
          />
          <SelectField
            label="Competência"
            value={competencia}
            onChange={(e) => filtrar(setCompetencia)(e.target.value)}
            options={[
              { value: "", label: "— TODAS —" },
              ...facetas.competencias.map((c) => ({ value: c, label: fmtCompetencia(c) })),
            ]}
          />
          <SelectField
            label="Usuário"
            value={ator}
            onChange={(e) => filtrar(setAtor)(e.target.value)}
            options={[
              { value: "", label: "— TODOS —" },
              ...facetas.atores.map((a) => ({ value: a.ator, label: a.nome })),
            ]}
          />
          <SelectField
            label="Situação"
            value={status}
            onChange={(e) => filtrar(setStatus)(e.target.value)}
            options={[
              { value: "", label: "— TODAS —" },
              ...Object.entries(SITUACAO).map(([k, v]) => ({ value: k, label: v.completo })),
            ]}
          />
        </div>
      </FilterBar>

      <DataTable
        caption="Histórico de arquivos trocados, com competência, tipo, quem enviou, a situação e a observação"
        columns={columns}
        rows={q.data?.arquivos ?? []}
        rowKey={(a) => a.id}
        loading={q.isLoading}
        emptyState={
          q.isError
            ? "Não foi possível carregar o histórico."
            : "Nenhum arquivo encontrado com esses filtros."
        }
      />

      <Pagination
        total={q.data?.total ?? 0}
        pagina={pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="registros"
      />
      {relatorio.modal}
    </div>
  );
}

// ============================================================ COMPETENCIAS

/** `2026-09-15T17:00:00-03:00` -> `15/09/2026 17:00`. */
function fmtInstante(iso: string | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const d = new Date(t);
  return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

const fmtData = (iso: string | null | undefined) => (iso ? iso.split("-").reverse().join("/") : "—");

/**
 * O ESTADO DE CADA COMPETENCIA — o que substituiu /prefeitura/folhas.
 *
 * So' leitura, e de proposito. A tela antiga tinha "Fechar folha", "Realinhar com
 * convenio" e um delete; os tres sairam:
 *
 *   "Fechar folha" — o unico efeito era habilitar a consolidacao, que e' ato da
 *   averbadora. A mao que autorizava nao era a que executava, e o botao confirmava
 *   a mao o que a regra ja sabia calcular. Hoje a folha fecha por FATO.
 *
 *   "Realinhar com convenio" — reescrevia corte e repasse sem registrar por que.
 *   O corte virou regra + excecao COM MOTIVO E AUTOR; um botao que sobrescreve
 *   data em silencio contradiz isso.
 *
 *   Excluir — a competencia tem movimento emitido e fatos de desconto apontando
 *   para ela. Nao e' rascunho.
 *
 * O que a prefeitura FAZ com a competencia esta nas outras abas desta mesma tela.
 */
/** O corte da folha e, antes dele, o arquivo da consignatária que ainda não chegou (#278). */
export function CorteDaFolha({ f }: { f: PrefeituraFolha }) {
  const e = f.estado;
  if (!e?.corteEm) return <span style={{ opacity: 0.5 }}>—</span>;
  return (
    <div style={{ display: "grid", gap: 2 }}>
      {/* INSTANTE, nao data. O comunicado real diz "dia 15, a partir das
          17:00": as 16:59 ainda entra averbacao, as 17:00 nao. */}
      <span style={{ fontSize: 12 }}>{fmtInstante(e.corteEm)}</span>
      <span style={{ fontSize: 11, color: e.cortePassou ? "var(--text-dim)" : "var(--gold-500)" }}>
        {e.cortePassou ? "corte passou" : "ainda entram averbações"}
        {e.corteSobrescrito ? " · exceção" : ""}
      </span>
      {(f.arquivosConsignatariaPendentes ?? []).map((p) => (
        <span
          key={p.credorId}
          title={p.detalhes}
          style={{ fontSize: 11, fontWeight: 600, color: p.severidade === "critico" ? "var(--danger-500)" : "var(--gold-500)" }}
        >
          Arquivo de {p.credor} pendente{p.severidade === "critico" ? " · véspera do corte" : ""}
        </span>
      ))}
    </div>
  );
}

function Competencias() {
  const [pagComp, setPagComp] = useState(1);
  const [ppComp, setPpComp] = useState(POR_PAGINA_PADRAO);
  // A organização ativa vem do cabeçalho (feature 009): esta aba lista as
  // competências DELA, e não mais sempre as da organização do login.
  const { unidade, opcoesDoSdk, chaveDaUnidade } = useUnidadeAtiva();
  const [compIni, setCompIni] = useState("");
  const [compFim, setCompFim] = useState("");
  const [status, setStatus] = useState("");
  const q = useQuery({
    queryKey: ["prefeitura", "folhas", ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.folhas(opcoesDoSdk()),
  });
  const folhas: PrefeituraFolha[] = q.data?.folhas ?? [];
  const [, setParams] = useSearchParams();

  // O layout de SAÍDA da organização ATIVA — não mais o da primeira unidade da
  // lista. Ele decide se há um segundo arquivo a oferecer: numa unidade no
  // padrão os dois seriam o mesmo, e o botão extra sugeriria uma diferença que
  // não existe. Ler o da organização errada oferecia (ou escondia) o botão pelo
  // layout de outra folha.
  const saidaAdaptada = unidade?.layouts?.saida.padrao === false;
  // Ate 04/09/2026 esta aba SO' PAGINAVA: achar a competencia de marco exigia
  // virar pagina ate topar com ela, num ciclo que ganha uma linha por mes e
  // nunca encolhe. A chave natural da lista e' justamente o mes, e o estado do
  // ciclo e' o que decide se ha algo a fazer.
  //
  // O recorte mora em `lib/filtro-folhas` porque o Historico de folhas, em
  // `prefeitura/relatorios`, lista ESTA MESMA consulta com outras colunas.
  const { filtradas, contagemStatus, totalSemStatus } = filtrarFolhas(folhas, { compIni, compFim, status });

  const filtros = useFiltros(
    [
      // O intervalo e' UMA dimensao, nao duas: o operador pensa "de janeiro a
      // marco". Contar 2 por haver dois campos mediria o formulario.
      { nome: "competencia", ligado: compIni !== "" || compFim !== "", limpar: () => { setCompIni(""); setCompFim(""); } },
      { nome: "status", ligado: status !== "", limpar: () => setStatus("") },
    ],
    setPagComp,
  );
  const { filtrar, temFiltro } = filtros;
  const daPagina = recortar(filtradas, pagComp, ppComp);

  const irPara = (aba: string, competencia: string) => {
    const p = new URLSearchParams();
    p.set("aba", aba);
    p.set("competencia", competencia);
    setParams(p, { replace: false });
  };

  const colunas: Column<PrefeituraFolha>[] = [
    {
      key: "competencia",
      header: "Competência",
      width: 150,
      render: (f) => (
        <div style={{ display: "grid", gap: 2 }}>
          <b>{fmtCompetencia(f.competencia)}</b>
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{f.competencia}</span>
        </div>
      ),
    },
    {
      key: "corte",
      header: "Corte",
      width: 165,
      render: (f) => <CorteDaFolha f={f} />,
    },
    {
      key: "movimento",
      header: "Movimento",
      width: 175,
      render: (f) =>
        f.movimentoEmitido ? (
          <div style={{ display: "grid", gap: 3 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Pill variant="averbado">{f.movimentoEmitido.retificacaoDe ? "retificado" : "emitido"}</Pill>
              <Button
                size="sm"
                variant="ghost"
                // Botão de uma seta só: sem `aria-label` o leitor de tela
                // anuncia "botão ↓" e quem ouve não sabe de qual competência
                // (T081, FR-039). O `title` não serve — nem sempre é lido.
                aria-label={`Baixar o movimento de ${fmtCompetencia(f.competencia)}`}
                title={`Baixa o movimento emitido em ${fmtInstante(f.movimentoEmitido.em)}. É o MESMO arquivo entregue, não uma geração nova.`}
                onClick={() =>
                  downloadAuthed(
                    atlas.prefeitura.movimentoUrl(f.competencia, opcoesDoSdk()),
                    `movimento_${f.competencia}.csv`,
                  )
                }
              >
                ↓
              </Button>
              {saidaAdaptada ? (
                <Button
                  size="sm"
                  variant="ghost"
                  title="O MESMO movimento no padrão Atlas. Ele continua disponível mesmo com um layout adaptado atribuído."
                  onClick={() =>
                    downloadAuthed(
                      atlas.prefeitura.movimentoUrl(f.competencia, { layout: "atlas", ...opcoesDoSdk() }),
                      `movimento_${f.competencia}.csv`,
                    )
                  }
                >
                  padrão
                </Button>
              ) : null}
            </div>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
              {fmtInstante(f.movimentoEmitido.em)}
            </span>
          </div>
        ) : (
          <span
            style={{ fontSize: 12, color: "var(--text-muted)" }}
            title="A Atlas emite o movimento no corte. Antes disso não há arquivo definitivo — averbações ainda entram."
          >
            aguardando a Atlas
          </span>
        ),
    },
    {
      key: "retorno",
      header: "Retorno",
      width: 200,
      render: (f) => <CelulaRetorno estado={f.estado} onEnviar={() => irPara("enviar", f.competencia)} />,
    },
    {
      key: "repasse",
      header: "Repasse",
      width: 130,
      render: (f) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 12 }}>{fmtData(f.dataRepasse)}</span>
          {/* #279: o que já foi registrado como repassado (detalhe em Repasses). */}
          {f.repasses && f.repasses.quantidade > 0 ? (
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
              {fmtBRL(f.repasses.repassadoCentavos / 100)} repassado
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "situacao",
      header: "Situação",
      width: 150,
      render: (f) => (
        <div style={{ display: "grid", gap: 2 }}>
          <Pill
            variant={f.status === "consolidada" ? "aceita" : f.status === "fechada" ? "averbado" : "pendente"}
          >
            {f.status}
          </Pill>
          {f.conferencia ? (
            <span
              style={{
                fontSize: 11,
                color: f.conferencia === "presumida" ? "var(--gold-500)" : "var(--text-dim)",
              }}
              title={
                f.conferencia === "presumida"
                  ? "Fechou sem o retorno completo: parte dos descontos foi presumida como aplicada."
                  : "O retorno chegou e foi conferido."
              }
            >
              {f.conferencia}
            </span>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <Card>
        <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
          O ciclo de cada mês: a Atlas <b>emite o movimento</b> no corte, você processa a folha e
          <b> envia o retorno</b>, e a Atlas confirma. Esta aba mostra em que ponto está cada competência — o
          que você <i>faz</i> está nas abas ao lado.
        </div>
      </Card>

      {q.isLoading ? <Card>Carregando…</Card> : null}
      {q.error ? <Card>Não foi possível carregar as competências.</Card> : null}


      {/* A barra so' aparece com base para recortar: um municipio no primeiro
          mes nao ganha nada com filtro de periodo, e um controle que nao corta
          nada e' ruido. Sem busca textual de proposito — competencia e situacao
          sao dimensoes tipadas, e nao ha identificador para procurar aqui. */}
      {folhas.length > 0 ? (
        <FilterBar
          layout="linha"
          {...filtros.propsFilterBar}
          actions={
            <FilterChips
              variant="segmentado"
              label="Situação"
              value={status}
              onChange={filtrar(setStatus)}
              options={[
                { value: "", label: "Todas", count: totalSemStatus },
                ...contagemStatus.map((s) => ({ value: s.valor, label: s.valor, count: s.contagem })),
              ]}
            />
          }
        >
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
            De
            <input
              type="month"
              value={compParaInput(compIni)}
              onChange={(e) => filtrar(setCompIni)(e.target.value)}
              style={{ ...inp, width: "auto" }}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
            até
            <input
              type="month"
              value={compParaInput(compFim)}
              onChange={(e) => filtrar(setCompFim)(e.target.value)}
              style={{ ...inp, width: "auto" }}
            />
          </label>
        </FilterBar>
      ) : null}

      {!q.isLoading ? (
        <>
          <DataTable
            columns={colunas}
            rows={daPagina.itens}
            rowKey={(f) => f.id}
            emptyState={
              <EmptyState
                motivo={motivoVazio(folhas.length, filtradas.length)}
                filtroVazio="Nenhuma competência nesse período ou situação."
                semDados="Nenhuma competência ainda. Ela aparece aqui quando a Atlas abre o mês."
                onLimpar={filtros.limparFiltros}
              />
            }
          />
          {folhas.length > 0 ? (
            <Pagination
              total={daPagina.total}
              pagina={daPagina.pagina}
              porPagina={ppComp}
              onPagina={setPagComp}
              onPorPagina={setPpComp}
              rotulo="competências"
            />
          ) : null}
          {temFiltro ? (
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Mostrando <b style={{ color: "var(--text)" }}>{filtradas.length}</b> de {folhas.length} competências.
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * O retorno da competencia, com as PARTES quando a folha manda mais de um arquivo.
 *
 * O numero de partes existe porque sem ele quem enviava so' a primeira nao tinha
 * como saber que a competencia continuava esperando a segunda. As partes ACUMULAM
 * e nao travam mais o fechamento (decisao do cliente, 28/08/2026) — entao aqui o
 * texto informa, nao acusa.
 *
 * `recebidosNaoProcessados` e' o estado novo e o mais importante de mostrar: o
 * arquivo CHEGOU e ainda nao foi aplicado. Antes disso existir, a prefeitura via
 * "enviado" e presumia "processado", que sao coisas diferentes.
 */
function CelulaRetorno({ estado, onEnviar }: { estado?: AdminEstadoDaFolha; onEnviar: () => void }) {
  if (!estado) return <span style={{ opacity: 0.5 }}>—</span>;
  const { partesEsperadas, partesRecebidas, partesFaltando, retornoCompleto, recebidosNaoProcessados } =
    estado;

  const emAnalise =
    recebidosNaoProcessados > 0 ? (
      <span
        style={{ fontSize: 11, color: "var(--gold-500)" }}
        title="O arquivo chegou e está guardado. A Atlas processa e confirma — até lá nada foi aplicado."
      >
        {recebidosNaoProcessados} em análise pela Atlas
      </span>
    ) : null;

  if (partesEsperadas.length === 0) {
    return (
      <div style={{ display: "grid", gap: 2 }}>
        {retornoCompleto ? (
          <Pill variant="aceita">processado</Pill>
        ) : recebidosNaoProcessados > 0 ? (
          <Pill variant="pendente">recebido</Pill>
        ) : (
          <Button size="sm" variant="ghost" onClick={onEnviar}>
            enviar retorno →
          </Button>
        )}
        {emAnalise}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 2 }}>
      <span style={{ fontSize: 12 }}>
        {partesRecebidas.length} de {partesEsperadas.length} partes
      </span>
      {partesFaltando.length > 0 ? (
        <span
          style={{ fontSize: 11, color: "var(--text-muted)" }}
          title={`Faltam: ${partesFaltando.join(", ")}`}
        >
          falta {partesFaltando.join(", ")}
        </span>
      ) : (
        <span style={{ fontSize: 11, color: "var(--emerald-500)" }}>completo</span>
      )}
      {emAnalise}
      {partesFaltando.length > 0 ? (
        <Button size="sm" variant="ghost" onClick={onEnviar}>
          enviar parte →
        </Button>
      ) : null}
    </div>
  );
}
